export const ONBOARDING_ROOT = '/onboarding';

// Bumped whenever the onboarding flow's shape changes meaningfully. Companies
// created in the v2 flow carry `data.onboardingFlowVersion === 2`; legacy
// companies have no value at all. The resume guard uses this to decide whether
// a user with `companyId` but no connected inbox should be pulled back to the
// email step. Pulling a legacy user back into a step that never existed for
// them would be wrong, so we deliberately scope it.
export const ONBOARDING_FLOW_VERSION = 2;

export const ONBOARDING_PATHS = {
    business: `${ONBOARDING_ROOT}/business`,
    // The "how it works" cinematic — plays right after the business name step and
    // BEFORE email connect (placeholder data, no live inbox yet). Ends on a
    // "Connect my inbox" CTA that hands off to the email step.
    animation: `${ONBOARDING_ROOT}/animation`,
    source: `${ONBOARDING_ROOT}/source`,
    email: `${ONBOARDING_ROOT}/email`,
    sms: `${ONBOARDING_ROOT}/sms`,
    smsTwilio: `${ONBOARDING_ROOT}/sms/twilio`,
    smsForward: `${ONBOARDING_ROOT}/sms/forward`,
    smsForwardContact: `${ONBOARDING_ROOT}/sms/forward/contact`,
    inboxLoading: `${ONBOARDING_ROOT}/inbox`,
    scanResults: `${ONBOARDING_ROOT}/scan-results`,
    included: `${ONBOARDING_ROOT}/whats-included`,
    upgrade: `${ONBOARDING_ROOT}/upgrade`,
    buyBox: `${ONBOARDING_ROOT}/buy-box`,
    buyBoxLocation: `${ONBOARDING_ROOT}/buy-box/location`,
    buyBoxBudget: `${ONBOARDING_ROOT}/buy-box/budget`,
    notifications: `${ONBOARDING_ROOT}/notifications`,
};

export const ONBOARDING_SCREENS = {
    business: 'business',
    animation: 'animation',
    source: 'source',
    email: 'email',
    sms: 'sms',
    inboxLoading: 'inboxLoading',
    scanResults: 'scanResults',
    included: 'included',
    upgrade: 'upgrade',
    buyBox: 'buyBox',
    buyBoxLocation: 'buyBoxLocation',
    buyBoxBudget: 'buyBoxBudget',
    notifications: 'notifications',
    invalid: 'invalid',
};

export const ONBOARDING_STORAGE_KEYS = {
    selectedChannel: 'clozers:onboarding:selectedChannel',
    smsPath: 'clozers:onboarding:smsPath',
    buyBoxGateChoice: 'clozers:onboarding:buyBoxGateChoice',
    benefitsSeen: 'clozers:onboarding:benefitsSeen',
    checkoutPending: 'clozers:onboarding:checkoutPending',
    dealVolume: 'clozers:onboarding:dealVolume',
};

// Durable (per-browser) marker that the user has watched the onboarding cinematic
// to the end and clicked its "Connect my inbox →" CTA at least once. This is the
// single gate for *every* auto-redirect to the email-connect step: a user who has
// never seen the animation must arrive at email *through* it, never be dropped
// straight onto it. localStorage (not sessionStorage) so it survives reloads and
// return visits — and it is intentionally NOT cleared by clearOnboardingStorage.
export const ONBOARDING_ANIMATION_SEEN_KEY = 'clozers:onboarding:animationSeen';

export const hasSeenOnboardingAnimation = () => {
    if (typeof window === 'undefined') return false;
    try { return window.localStorage.getItem(ONBOARDING_ANIMATION_SEEN_KEY) === '1'; }
    catch { return false; }
};

export const markOnboardingAnimationSeen = () => {
    if (typeof window === 'undefined') return;
    try { window.localStorage.setItem(ONBOARDING_ANIMATION_SEEN_KEY, '1'); }
    catch { /* ignore */ }
};

const normalizePathname = (pathname = '') => {
    const pathOnly = pathname.split('?')[0].split('#')[0] || ONBOARDING_ROOT;
    return pathOnly.length > 1 ? pathOnly.replace(/\/+$/, '') : pathOnly;
};

// A workspace-having user with nowhere specific to be lands on the cinematic
// (animation) until they've completed it once — only then does "default" mean the
// email step. Workspace-less users always start at the business-name step.
export const getDefaultOnboardingPath = (hasWorkspace, animationSeen = false) => {
    if (!hasWorkspace) return ONBOARDING_PATHS.business;
    return animationSeen ? ONBOARDING_PATHS.email : ONBOARDING_PATHS.animation;
};

export const getOnboardingRoute = (pathname) => {
    const path = normalizePathname(pathname);

    switch (path) {
        case ONBOARDING_PATHS.business:
            return { screen: ONBOARDING_SCREENS.business, visualStep: 0, valid: true };
        case ONBOARDING_PATHS.animation:
            return { screen: ONBOARDING_SCREENS.animation, visualStep: 1, valid: true };
        case ONBOARDING_PATHS.source:
            return { screen: ONBOARDING_SCREENS.source, visualStep: 1, valid: true };
        case ONBOARDING_PATHS.email:
            return { screen: ONBOARDING_SCREENS.email, visualStep: 1, valid: true };
        case ONBOARDING_PATHS.sms:
            return { screen: ONBOARDING_SCREENS.sms, smsSubStep: 0, smsPath: null, visualStep: 1, valid: true };
        case ONBOARDING_PATHS.smsTwilio:
            return { screen: ONBOARDING_SCREENS.sms, smsSubStep: 1, smsPath: 'twilio', visualStep: 1, valid: true };
        case ONBOARDING_PATHS.smsForward:
            return { screen: ONBOARDING_SCREENS.sms, smsSubStep: 1, smsPath: 'forward', visualStep: 1, valid: true };
        case ONBOARDING_PATHS.smsForwardContact:
            return { screen: ONBOARDING_SCREENS.sms, smsSubStep: 2, smsPath: 'forward', visualStep: 1, valid: true };
        case ONBOARDING_PATHS.inboxLoading:
            return { screen: ONBOARDING_SCREENS.inboxLoading, visualStep: 2, valid: true };
        case ONBOARDING_PATHS.scanResults:
            return { screen: ONBOARDING_SCREENS.scanResults, visualStep: 2, valid: true };
        case ONBOARDING_PATHS.included:
            return { screen: ONBOARDING_SCREENS.included, visualStep: 2, valid: true };
        case ONBOARDING_PATHS.upgrade:
            return { screen: ONBOARDING_SCREENS.upgrade, visualStep: 2, valid: true };
        case ONBOARDING_PATHS.buyBox:
            return { screen: ONBOARDING_SCREENS.buyBox, visualStep: 2, valid: true };
        case ONBOARDING_PATHS.buyBoxLocation:
            return { screen: ONBOARDING_SCREENS.buyBoxLocation, buyBoxSubStep: 0, visualStep: 2, valid: true };
        case ONBOARDING_PATHS.buyBoxBudget:
            return { screen: ONBOARDING_SCREENS.buyBoxBudget, buyBoxSubStep: 1, visualStep: 2, valid: true };
        case ONBOARDING_PATHS.notifications:
            return { screen: ONBOARDING_SCREENS.notifications, visualStep: 2, valid: true };
        default:
            return { screen: ONBOARDING_SCREENS.invalid, visualStep: 0, valid: false };
    }
};

export const resolveOnboardingRedirect = (pathname, hasWorkspace, animationSeen = false) => {
    const path = normalizePathname(pathname);
    const route = getOnboardingRoute(path);

    if (path === ONBOARDING_ROOT || !route.valid) {
        return getDefaultOnboardingPath(hasWorkspace, animationSeen);
    }

    if (hasWorkspace && route.screen === ONBOARDING_SCREENS.business) {
        // Cinematic-not-yet-seen → send them through it first; only a user who has
        // already watched it to the end + clicked its CTA jumps straight to email.
        return animationSeen ? ONBOARDING_PATHS.email : ONBOARDING_PATHS.animation;
    }

    if (!hasWorkspace && route.screen !== ONBOARDING_SCREENS.business) {
        return ONBOARDING_PATHS.business;
    }

    return null;
};

export const routeRequiresIntegration = (route) => (
    route?.screen === ONBOARDING_SCREENS.inboxLoading ||
    route?.screen === ONBOARDING_SCREENS.scanResults ||
    route?.screen === ONBOARDING_SCREENS.buyBox ||
    route?.screen === ONBOARDING_SCREENS.buyBoxLocation ||
    route?.screen === ONBOARDING_SCREENS.buyBoxBudget ||
    route?.screen === ONBOARDING_SCREENS.notifications
);

export const routeRequiresForwardPhone = (route) => (
    route?.screen === ONBOARDING_SCREENS.sms &&
    route.smsSubStep === 2 &&
    route.smsPath === 'forward'
);

// First unfinished onboarding step for a user who's NOT inside /onboarding.
// Used by RequireOnboarding to pull abandoned signups back to where they left off.
//
// Scope rules:
//  - No companyId  → always /onboarding/business (universal: legacy + new).
//                    We can't ship product to a user without a workspace.
//  - companyId + new-flow company + user is the company admin + no inbox
//                  → the cinematic (/onboarding/animation) if they've never watched
//                    it through, else /onboarding/email. Same gate as every other
//                    auto-redirect: a user who hasn't seen the animation is never
//                    dropped straight onto the email step. Legacy companies (no
//                    onboardingFlowVersion field) keep their current behaviour.
//                    Non-admin invited members are never pulled into onboarding —
//                    OnboardingFlow itself sends them back to '/' and a circular
//                    redirect would result.
//  - Otherwise → null (post-payment resume lives inside OnboardingFlow itself).
export const getResumeOnboardingPath = ({ user, companyId, unipileAccounts, companyData, animationSeen = false }) => {
    if (companyData?.onboardingCompleted || companyData?.notifications) return null;

    if (!companyId) return ONBOARDING_PATHS.business;

    const isCompanyAdmin = Array.isArray(companyData?.users)
        && companyData.users.some(u => u.userId === user?.userId && u.admin === true);
    if (!isCompanyAdmin) return null;

    const isNewFlowCompany = companyData?.onboardingFlowVersion === ONBOARDING_FLOW_VERSION;
    if (!isNewFlowCompany) return null;

    const emailConnected = Array.isArray(unipileAccounts) && unipileAccounts.length > 0;
    if (!emailConnected) {
        // Auto-disconnected unpaid returners get the scan-replay (results from
        // their first scan are preserved at companies/{id}/onboardingJobs/firstEmailDeal)
        // and are funnelled toward the paywall instead of being asked to
        // re-integrate. Post-pay reconnect is handled inside OnboardingFlow itself
        // — once hasPaid + no accounts, its existing routeRequiresIntegration
        // gate forces the email step.
        if (companyData?.emailIntegrationStatus === 'auto_disconnected') {
            return ONBOARDING_PATHS.scanResults;
        }
        // Never seen the cinematic → send them through it first; it ends on the
        // "Connect my inbox →" CTA that hands off to the email step.
        return animationSeen ? ONBOARDING_PATHS.email : ONBOARDING_PATHS.animation;
    }

    return null;
};

export const clearOnboardingStorage = (storage) => {
    const targetStorage = storage || (typeof window !== 'undefined' ? window.sessionStorage : null);
    if (!targetStorage) return;
    Object.values(ONBOARDING_STORAGE_KEYS).forEach((key) => targetStorage.removeItem(key));
};
